"use client";

import React from "react";
import { Clock, MonitorPlay, BarChart, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const courseModules = [{
  title: "Introducción y Anatomía Palpatoria",
  content: "Fundamentos de la masoterapia, ética profesional y estudio anatómico detallado. Aprenderás a identificar estructuras musculares y óseas mediante el tacto para una práctica segura."
}, {
  title: "Masaje Sueco y de Relajación",
  content: "Dominio de las técnicas clásicas para el alivio del estrés. Incluye maniobras de deslizamiento, amasamiento y percusión diseñadas para promover el bienestar integral."
}, {
  title: "Masaje Descontracturante",
  content: "Tratamiento de contracturas y puntos gatillo. Aprenderás técnicas de presión profunda dirigidas a liberar tensiones musculares crónicas y mejorar la movilidad del paciente."
}, {
  title: "Drenaje Linfático Manual",
  content: "Especialización en el sistema linfático. Técnicas suaves y rítmicas para favorecer la eliminación de toxinas, reducir inflamaciones y mejorar la respuesta inmunitaria."
}, {
  title: "Masaje Deportivo y Estiramientos",
  content: "Atención al deportista en todas sus fases. Técnicas para prevenir lesiones, acelerar la recuperación post-esfuerzo y optimizar el rendimiento físico mediante estiramientos asistidos."
}];
const courseFeatures = [{
  icon: Clock,
  label: "Duración",
  value: "12 Semanas"
}, {
  icon: MonitorPlay,
  label: "Modalidad",
  value: "100% Presencial"
}, {
  icon: BarChart,
  label: "Nivel",
  value: "Intermedio"
}];
export default function DetallesDelCurso() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="detalles-del-curso" ref={sectionRef} className="bg-background relative py-16 md:py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          
          {}
          <div className={`lg:col-span-8 space-y-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                Temario del Curso
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl">
                Un programa estructurado paso a paso, diseñado para llevarte desde los conceptos fundamentales hasta el dominio avanzado de las herramientas.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {courseModules.map((module, i) => <AccordionItem key={i} value={`module-${i}`} data-index={i} className="bg-background/60 backdrop-blur-md border border-border/60 rounded-2xl px-2 sm:px-6 shadow-sm data-[state=open]:bg-background/90 data-[state=open]:shadow-md transition-all duration-300 overflow-hidden">
                  <AccordionTrigger className="hover:no-underline py-6 group">
                    <div className="flex items-center gap-4 sm:gap-6 text-left w-full pr-4">
                      <span className="font-mono text-primary font-bold text-xl sm:text-2xl opacity-80 group-hover:opacity-100 transition-opacity">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-semibold text-foreground text-base sm:text-lg">
                        {module.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6 pl-14 sm:pl-16 text-sm sm:text-base">
                    {module.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className={`lg:col-span-4 transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="sticky top-24">
              <Card className="rounded-[24px] border-border/50 shadow-xl shadow-shadow-color/5 bg-background/80 backdrop-blur-xl overflow-hidden">
                <CardHeader className="pb-4 border-b border-border/50 bg-muted/30">
                  <CardTitle className="text-xl font-bold text-foreground">
                    Resumen del Programa
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="pt-6 space-y-6">
                  <ul className="space-y-4">
                    {courseFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <feature.icon className="w-5 h-5 text-primary/70" />
                          <span className="font-medium">{feature.label}</span>
                        </div>
                        <span className="font-mono font-semibold text-foreground">
                          {feature.value}
                        </span>
                      </li>)}
                  </ul>

                  <div className="pt-4 border-t border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-muted-foreground font-medium">Inversión</span>
                      <span className="font-mono text-3xl font-bold text-secondary drop-shadow-sm">$U 15.000</span>
                    </div>
                    <p className="text-xs text-muted-foreground text-right">
                      *Pago único. Acceso de por vida.
                    </p>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Certificado de finalización incluido</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Soporte directo con instructores</span>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="pt-2 pb-6 flex-col gap-4">
                  <Link to="https://wa.me/59896344395" className="w-full">
                    <Button size="lg" className="w-full h-14 text-base font-bold rounded-xl transition-all duration-300 hover:scale-[1.02] bg-[#25D366] text-white hover:bg-[#25D366]/90 hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] group">
                      WhatsApp
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="#" className="w-full">
                    <Button size="lg" className="w-full h-14 text-base font-bold rounded-xl transition-all duration-300 hover:scale-[1.02] bg-chart-3 text-primary-foreground hover:bg-chart-3/90 hover:shadow-[0_0_20px_var(--color-chart-3)] hover:shadow-chart-3/40 group">
                      Mercado Pago
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>
          </div>

        </div>
      </div>
    </section>;
}